/**
 * TemplateViewerPage
 *
 * Special viewer page for previewing training templates without importing.
 * Works with TemplateWorkspaceManager to display multiple templates simultaneously.
 */

import React, { useMemo } from "react";
import { ViewerCanvasWrapper } from "@/components/shared/rendering/ViewerCanvasWrapper";
import { Component, Viewport } from "@/types";
import { AlertTriangle, FileJson } from "lucide-react";
import { cn } from "@/lib/utils";
import { useViewerStore } from "@/stores/viewer/viewer-store";
import { useResponsiveViewport } from "@/hooks/useResponsiveViewport";
import {
  TEMPLATE_VIEWER_DEFAULT_CANVAS_SETTINGS,
  useTemplateWorkspace,
} from "@/hooks/useTemplateWorkspace";
import { TemplateCompactSelector } from "@/components/template/TemplateSelector";

export const TemplateViewerPage: React.FC = () => {
  // Detect responsive breakpoint dynamically
  const responsiveBreakpoint = useResponsiveViewport();
  const { addTemplate, allTemplates } = useTemplateWorkspace();
  // Get components and settings from viewer store (loaded by TemplateWorkspaceManager)
  const storeComponents = useViewerStore((state) => state.components);
  const storeCanvasSettings = useViewerStore((state) => state.canvasSettings);
  const storePages = useViewerStore((state) => state.pages);
  const storeIsLoading = useViewerStore((state) => state.isLoading);
  const storeError = useViewerStore((state) => state.error);

  // Get enhanced components from viewer store
  const { components, canvasSettings } = useMemo(() => {
    // Use enhanced components from store if available
    if (storeComponents && storeComponents.length > 0) {
      return {
        components: storeComponents,
        canvasSettings: storeCanvasSettings || TEMPLATE_VIEWER_DEFAULT_CANVAS_SETTINGS,
      };
    }

    // Fallback to empty state while loading
    return {
      components: [] as Component[],
      canvasSettings: TEMPLATE_VIEWER_DEFAULT_CANVAS_SETTINGS,
    };
  }, [storeComponents, storeCanvasSettings]);

  // Convert responsive breakpoint to Viewport enum
  const currentViewport: Viewport = useMemo(() => {
    switch (responsiveBreakpoint) {
      case 'mobile':
        return Viewport.MOBILE;
      case 'tablet':
        return Viewport.TABLET;
      case 'desktop':
      default:
        return Viewport.DESKTOP;
    }
  }, [responsiveBreakpoint]);

  // Loading state
  if (storeIsLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <div className="text-muted-foreground">Loading templates...</div>
        </div>
      </div>
    );
  }

  // Error state
  if (storeError) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <div className="rounded-full bg-destructive/10 p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-destructive" />
          </div>
          <h2 className="text-xl font-semibold mb-2">Failed to Load Template</h2>
          <p className="text-muted-foreground mb-4">{storeError}</p>
        </div>
      </div>
    );
  }

  // No templates loaded state
  if (storePages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <div className="rounded-full bg-blue-50 dark:bg-blue-900 p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <FileJson className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          </div>
          <h2 className="text-xl font-semibold mb-2">No Templates Loaded</h2>
          <p className="text-muted-foreground mb-4">
            Select a template to preview it in the mobile viewer.
          </p>
          <TemplateCompactSelector
            selectedTemplateId={null}
            onTemplateSelect={addTemplate}
            className="w-full"
            placeholder="Select template..."
            availableTemplates={allTemplates}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Viewer Canvas - render template directly without backend */}
      <div className="flex-1 relative overflow-auto min-h-0">
        <div
          className={cn(
            "viewer-canvas-unified relative",
            "flex justify-start items-start"
          )}
        >
          <ViewerCanvasWrapper
            key={`viewer-${currentViewport}`}
            components={components}
            canvasSettings={canvasSettings}
            viewport={currentViewport}
            enableInteractions={true}
          />
        </div>
      </div>
    </div>
  );
};
